import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ContactSection from '../components/ContactSection';
import { PoundSterling, CheckCircle2, HelpCircle, Camera, Scan, Boxes, Plane } from 'lucide-react';
import { breadcrumbSchema } from '../utils/structuredData';

const PricingPage = () => {
  const packages = [
    {
      icon: Camera,
      name: 'Matterport 3D Tours',
      startingPrice: '295',
      description: 'Immersive 3D virtual tours for property marketing, facilities management and documentation.',
      includes: [
        'Professional Matterport Pro3 scanning',
        'Hosted 3D virtual tour with unique URL',
        'Embed code for your website',
        'Dollhouse and floor plan views',
        'Measurement tools enabled',
        'Delivery within 24-48 hours'
      ],
      notes: 'Price based on properties up to 200 sqm. Larger spaces quoted individually.',
      link: '/matterport-virtual-tours-london',
      popular: false
    },
    {
      icon: Scan,
      name: 'Point Cloud Surveys',
      startingPrice: '495',
      description: 'Survey-grade laser scanning for as-built documentation, renovation planning and spatial analysis.',
      includes: [
        'Leica/FARO survey-grade laser scanning',
        'Registered and coloured point cloud dataset',
        'Deliverables in RCP, E57 or LAS formats',
        '2mm accuracy at typical survey ranges',
        'Optional 2D CAD drawings',
        'Delivery within 3-5 working days'
      ],
      notes: 'Price based on single-room or small area surveys. Multi-room and full-building surveys quoted individually.',
      link: '/point-cloud-surveys-london',
      popular: true
    },
    {
      icon: Boxes,
      name: 'Scan to BIM',
      startingPrice: '795',
      description: 'Revit BIM modelling from point cloud data, compliant with ISO 19650 and PAS 1192 standards.',
      includes: [
        'Point cloud survey included',
        'Revit model at specified LOD (200-500)',
        'Architectural, structural or MEP modelling',
        'IFC export for open BIM collaboration',
        'Quality assurance with deviation analysis',
        'ISO 19650 compliant deliverables'
      ],
      notes: 'Price based on LOD 200 for small spaces. Higher LOD levels and larger buildings quoted individually.',
      link: '/scan-to-bim-london',
      popular: false
    },
    {
      icon: Plane,
      name: 'Drone Surveys',
      startingPrice: 'POA',
      description: 'Aerial 3D scanning, photogrammetry and photography for sites, roofs and large-scale surveys.',
      includes: [
        'CAA-licensed drone operators',
        'Aerial photography and videography',
        'Photogrammetric 3D models and orthomosaics',
        'Roof and facade inspection',
        'Topographic survey data',
        'Integration with ground-level scans'
      ],
      notes: 'All drone operations subject to CAA regulations and site-specific risk assessment. Contact us for a tailored quote.',
      link: '/construction',
      popular: false
    }
  ];

  const addOns = [
    { service: '2D CAD Floor Plans', price: 'From £150' },
    { service: 'Schematic Floor Plans', price: 'From £95' },
    { service: 'Google Street View Integration', price: '£75' },
    { service: 'Mattertag Annotations', price: 'From £50' },
    { service: 'Guided Tour Creation', price: '£95' },
    { service: 'Custom Branding Package', price: '£150' },
    { service: 'Rush Delivery (Same Day)', price: '£150 surcharge' },
    { service: 'Weekend or Out-of-Hours Scanning', price: '25% surcharge' }
  ];

  const faqs = [
    {
      question: 'What factors affect the final price?',
      answer: 'The main factors are the size of the space (measured in square metres or number of scan positions), the type of deliverables required (virtual tour, point cloud, BIM model, CAD drawings), the level of detail or LOD for BIM projects, and any access constraints such as working at height or out-of-hours requirements. We provide detailed quotes after understanding your specific requirements.'
    },
    {
      question: 'Do you offer volume discounts for multiple properties?',
      answer: 'Yes. We offer discounted rates for clients with multiple properties or ongoing scanning requirements. Whether you are an agent with a portfolio of listings, a facilities manager with multiple buildings, or a contractor with regular project needs, we can structure pricing to reflect the volume. Contact us to discuss a tailored arrangement.'
    },
    {
      question: 'Is there a minimum charge?',
      answer: 'Our minimum charge for any scanning project is £295, which covers a standard Matterport scan of a smaller space (up to approximately 200 sqm). For point cloud surveys and Scan to BIM projects, minimum charges are £495 and £795 respectively.'
    },
    {
      question: 'What is included in the price?',
      answer: 'All quoted prices include the on-site scanning, data processing, quality assurance and delivery of the specified outputs. Travel within Greater London is included. For projects outside London, travel costs are quoted separately. Matterport hosting fees for the first 12 months are included in our tour prices.'
    },
    {
      question: 'How do I get a quote for my project?',
      answer: 'Contact us via the form below, by email at lee@digitalscanninggroup.com, or by phone. Provide the address, approximate size of the space, and the deliverables you need. We typically respond with a detailed quote within a few hours during business days.'
    },
    {
      question: 'What payment terms do you offer?',
      answer: 'We accept payment by bank transfer and major credit cards. For standard projects, we invoice on completion with 14-day payment terms. For larger projects, we can arrange staged payments aligned with project milestones. We also offer 30-day accounts for regular clients subject to credit approval.'
    }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "3D Scanning Services",
    "name": "3D Scanning Pricing - Digital Scanning Group",
    "provider": {
      "@type": "Organization",
      "name": "Digital Scanning Group",
      "url": "https://www.digitalscanninggroup.com"
    },
    "areaServed": [
      { "@type": "Place", "name": "London" },
      { "@type": "Place", "name": "United Kingdom" }
    ],
    "description": "Transparent pricing for Matterport virtual tours, point cloud surveys, Scan to BIM and drone surveys. Professional 3D scanning services in London and across the UK.",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "3D Scanning Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": "Matterport 3D Tours" },
          "priceCurrency": "GBP",
          "price": "295",
          "priceSpecification": { "@type": "UnitPriceSpecification", "priceCurrency": "GBP", "price": "295", "unitText": "per project" }
        },
        {
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": "Point Cloud Surveys" },
          "priceCurrency": "GBP",
          "price": "495",
          "priceSpecification": { "@type": "UnitPriceSpecification", "priceCurrency": "GBP", "price": "495", "unitText": "per project" }
        },
        {
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": "Scan to BIM" },
          "priceCurrency": "GBP",
          "price": "795",
          "priceSpecification": { "@type": "UnitPriceSpecification", "priceCurrency": "GBP", "price": "795", "unitText": "per project" }
        }
      ]
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="3D Scanning Pricing | Matterport, Point Cloud & BIM Services London"
        description="Transparent pricing for 3D scanning services. Matterport tours from £295, point cloud surveys from £495, Scan to BIM from £795."
        keywords="3D scanning pricing, matterport cost london, point cloud survey price, scan to bim cost, laser scanning quote, 3D tour pricing"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/pricing` : ''}
        structuredData={[
          serviceSchema,
          faqSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Pricing', url: typeof window !== 'undefined' ? `${window.location.origin}/pricing` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 rounded-full mb-6">
              <PoundSterling className="h-6 w-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">PRICING</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              3D Scanning Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent, competitive pricing for all our 3D scanning services. Every project is different, so we provide tailored quotes — but the starting prices below give you a clear indication of costs. For a more detailed breakdown, read our guide on <Link to="/blog/how-much-does-matterport-scan-cost-uk" className="text-blue-600 hover:underline">how much a Matterport scan costs in the UK</Link>.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {packages.map((pkg, index) => {
              const Icon = pkg.icon;
              return (
                <div key={index} className={`relative bg-white border-2 ${pkg.popular ? 'border-blue-500 shadow-xl shadow-blue-500/10' : 'border-gray-100'} rounded-2xl p-8 flex flex-col`}>
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-600 text-white text-sm font-semibold rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div className="inline-flex p-3 bg-blue-100 rounded-xl mb-4 self-start">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h2>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                  <div className="mb-6">
                    {pkg.startingPrice === 'POA' ? (
                      <div className="text-3xl font-bold text-gray-900">POA</div>
                    ) : (
                      <div>
                        <span className="text-sm text-gray-500">From</span>
                        <div className="text-3xl font-bold text-gray-900">£{pkg.startingPrice}</div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 mb-6 flex-grow">
                    {pkg.includes.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mb-4">{pkg.notes}</p>
                  <Link
                    to={pkg.link}
                    className="w-full text-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300 block"
                  >
                    Learn More
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Add-Ons */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Additional Services and Add-Ons</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Enhance your scanning project with these optional extras. All prices are in addition to the base scanning fee.
            </p>
            <div className="max-w-3xl mx-auto">
              <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
                {addOns.map((addOn, index) => (
                  <div key={index} className={`flex items-center justify-between p-6 ${index < addOns.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <span className="text-gray-700 font-medium">{addOn.service}</span>
                    <span className="text-blue-600 font-bold">{addOn.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Understanding Our Pricing */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Understanding Our Pricing</h2>
            <div className="max-w-3xl mx-auto space-y-4 text-gray-600 leading-relaxed">
              <p>
                Our pricing reflects the professional equipment, expertise and processing involved in delivering high-quality 3D scanning outputs. We use survey-grade Leica and FARO laser scanners alongside the latest Matterport Pro3 cameras — equipment that costs tens of thousands of pounds and requires ongoing calibration and maintenance.
              </p>
              <p>
                Every project includes a dedicated project manager, on-site scanning by experienced technicians, post-processing and quality assurance, and delivery of your specified outputs. For <Link to="/matterport-virtual-tours-london" className="text-blue-600 hover:underline">Matterport virtual tours</Link>, this includes 12 months of hosting. For <Link to="/point-cloud-surveys-london" className="text-blue-600 hover:underline">point cloud surveys</Link>, it includes registration, colourisation and export in your required formats. For <Link to="/scan-to-bim-london" className="text-blue-600 hover:underline">Scan to BIM</Link> projects, it includes the survey and the Revit modelling to your specified LOD.
              </p>
              <p>
                If you are working on a <Link to="/blog/bim-level-2-requirements-uk" className="text-blue-600 hover:underline">BIM-mandated project</Link>, our Scan to BIM pricing includes compliance documentation. For <Link to="/commercial-property-3d-tours-london" className="text-blue-600 hover:underline">commercial property agents</Link> with multiple listings, we offer portfolio pricing that reduces the per-property cost significantly. <Link to="/blog/drone-survey-costs-uk" className="text-blue-600 hover:underline">Drone survey costs</Link> vary depending on the scope and regulatory requirements of each site.
              </p>
            </div>
          </div>

          {/* Custom Quote CTA */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12 text-center mb-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Need a Custom Quote?</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Every project is unique. Contact us with your requirements and we will provide a detailed, no-obligation quote within a few hours. We are happy to visit your site for a free assessment on larger projects.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:lee@digitalscanninggroup.com"
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </a>
              <a
                href="tel:+447577705477"
                className="inline-block px-8 py-4 bg-white border-2 border-blue-600 text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all duration-300"
              >
                Call 07577 705477
              </a>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Pricing FAQ</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Related Pages */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Explore Our Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/matterport-virtual-tours-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Camera className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Matterport Virtual Tours</h3>
                <p className="text-gray-600 text-sm">Immersive 3D tours for property marketing and documentation.</p>
              </Link>
              <Link to="/point-cloud-surveys-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Scan className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Point Cloud Surveys</h3>
                <p className="text-gray-600 text-sm">Survey-grade laser scanning with 2mm accuracy.</p>
              </Link>
              <Link to="/scan-to-bim-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Boxes className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Scan to BIM</h3>
                <p className="text-gray-600 text-sm">Revit modelling from point cloud data at LOD 200-500.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default PricingPage;
